/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { TransferInvoicePreview } from '../models/transfer-invoice-preview';


/**
 * Administrative and preview tooling not exposed to normal users.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation previewInvoicePdf
   */
  static readonly PreviewInvoicePdfPath = '/admin/invoices/{id}/preview-pdf';

  /**
   * Preview an invoice document as a PDF.
   *
   * Renders the requested document through the same pipeline as the
   * matching generate workflow (GenerateOfferWorkflow for `offer`,
   * GenerateInvoiceDocumentsWorkflow's build → parse → render path
   * for `invoice` / `subfinancing_invoice`, and the transfer invoice
   * builder for `transfer_invoice`) but streams the resulting
   * PDF bytes directly in the response — no attachments are written,
   * no blobs are uploaded, no invoice or reference number is
   * persisted, and no domain events are emitted. A rendered preview is
   * audit-logged with its partner variant; a `transfer_invoice`
   * preview, including one that ends in a build error, is logged with
   * the invoice's configuration name.
   *
   * Requires the `invoice-docs:preview` permission (403 otherwise).
   *
   * `422` answers a document that cannot be built:
   *
   * - for `kind=transfer_invoice`, a transfer invoice build error;
   *   `error_code` is one of the `TransferInvoiceBuildErrorCode`
   *   values, the same codes
   *   `POST /admin/invoices/{id}/preview-transfer-invoice` returns;
   * - an InvoiceConfiguration whose view name cannot render the
   *   requested kind (for `invoice` and `transfer_invoice`, its invoice
   *   view name), or an `invoice` preview of an invoice without a
   *   finance date. These carry no `error_code`.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `previewInvoicePdf()` instead.
   *
   * This method doesn't expect any request body.
   */
  previewInvoicePdf$Response(params: {
    id: number;

    /**
     * Document kind to preview. &#x60;offer&#x60; renders the offer PDF;
     * &#x60;invoice&#x60; and &#x60;subfinancing_invoice&#x60; build the Finvoice in
     * memory and render the invoice PDF from the parsed Finvoice
     * (the subfinancing variant substitutes today&#x27;s date when the
     * finance date is missing). &#x60;transfer_invoice&#x60; builds the
     * transfer invoice (the same document
     * &#x60;POST /admin/invoices/{id}/preview-transfer-invoice&#x60; returns
     * as XML) and renders its PDF from the parsed Finvoice. All paths
     * write nothing. Additional kinds (general_terms, final_report)
     * will be added later.
     */
    kind: 'offer' | 'invoice' | 'subfinancing_invoice' | 'transfer_invoice';

    /**
     * Overrides the partner variant. When omitted, the variant is
     * taken from the invoice&#x27;s InvoiceConfiguration. For an invoice
     * without a configuration, &#x60;offer&#x60; needs a variant (400 without
     * one), and &#x60;invoice&#x60; and &#x60;subfinancing_invoice&#x60; answer 400
     * (&quot;invoice has no configuration; cannot build the Finvoice&quot;)
     * whatever the variant, because they build the Finvoice from the
     * configuration. Not accepted with &#x60;kind&#x3D;transfer_invoice&#x60; (400):
     * the transfer invoice is always built with the invoice&#x27;s own
     * configuration.
     */
    variant?: 'corona' | 'arex' | 'front' | 'lumi' | 'svea';
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Blob>> {

    const rb = new RequestBuilder(this.rootUrl, AdminService.PreviewInvoicePdfPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('kind', params.kind, {});
      rb.query('variant', params.variant, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/pdf',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Preview an invoice document as a PDF.
   *
   * Renders the requested document through the same pipeline as the
   * matching generate workflow (GenerateOfferWorkflow for `offer`,
   * GenerateInvoiceDocumentsWorkflow's build → parse → render path
   * for `invoice` / `subfinancing_invoice`, and the transfer invoice
   * builder for `transfer_invoice`) but streams the resulting
   * PDF bytes directly in the response — no attachments are written,
   * no blobs are uploaded, no invoice or reference number is
   * persisted, and no domain events are emitted. A rendered preview is
   * audit-logged with its partner variant; a `transfer_invoice`
   * preview, including one that ends in a build error, is logged with
   * the invoice's configuration name.
   *
   * Requires the `invoice-docs:preview` permission (403 otherwise).
   *
   * `422` answers a document that cannot be built:
   *
   * - for `kind=transfer_invoice`, a transfer invoice build error;
   *   `error_code` is one of the `TransferInvoiceBuildErrorCode`
   *   values, the same codes
   *   `POST /admin/invoices/{id}/preview-transfer-invoice` returns;
   * - an InvoiceConfiguration whose view name cannot render the
   *   requested kind (for `invoice` and `transfer_invoice`, its invoice
   *   view name), or an `invoice` preview of an invoice without a
   *   finance date. These carry no `error_code`.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `previewInvoicePdf$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  previewInvoicePdf(params: {
    id: number;

    /**
     * Document kind to preview. &#x60;offer&#x60; renders the offer PDF;
     * &#x60;invoice&#x60; and &#x60;subfinancing_invoice&#x60; build the Finvoice in
     * memory and render the invoice PDF from the parsed Finvoice
     * (the subfinancing variant substitutes today&#x27;s date when the
     * finance date is missing). &#x60;transfer_invoice&#x60; builds the
     * transfer invoice (the same document
     * &#x60;POST /admin/invoices/{id}/preview-transfer-invoice&#x60; returns
     * as XML) and renders its PDF from the parsed Finvoice. All paths
     * write nothing. Additional kinds (general_terms, final_report)
     * will be added later.
     */
    kind: 'offer' | 'invoice' | 'subfinancing_invoice' | 'transfer_invoice';

    /**
     * Overrides the partner variant. When omitted, the variant is
     * taken from the invoice&#x27;s InvoiceConfiguration. For an invoice
     * without a configuration, &#x60;offer&#x60; needs a variant (400 without
     * one), and &#x60;invoice&#x60; and &#x60;subfinancing_invoice&#x60; answer 400
     * (&quot;invoice has no configuration; cannot build the Finvoice&quot;)
     * whatever the variant, because they build the Finvoice from the
     * configuration. Not accepted with &#x60;kind&#x3D;transfer_invoice&#x60; (400):
     * the transfer invoice is always built with the invoice&#x27;s own
     * configuration.
     */
    variant?: 'corona' | 'arex' | 'front' | 'lumi' | 'svea';
    context?: HttpContext
  }
): Observable<Blob> {

    return this.previewInvoicePdf$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

  /**
   * Path part for operation previewTransferInvoice
   */
  static readonly PreviewTransferInvoicePath = '/admin/invoices/{id}/preview-transfer-invoice';

  /**
   * Preview the transfer invoice built for an invoice.
   *
   * Builds the transfer invoice for the invoice exactly as it would be
   * built for sending: the seller's own invoice, with the invoice's
   * InvoiceConfiguration as the payee, Corona Capital as the sender,
   * Corona's `new_number` and `new_reference_number` as the invoice
   * number and payment reference, and the configuration's transfer
   * statement added. The source track is chosen by data (see
   * `TransferInvoiceTrack`).
   *
   * The response carries the track, the source Finvoice (on the
   * `original` track), the transfer invoice Finvoice, the schema
   * validation of both, and the findings: disagreements between the
   * original and the invoice record, and schema defects. A finding
   * does not stop the document from being built. The rendered PDF of
   * the same document is
   * `POST /admin/invoices/{id}/preview-pdf?kind=transfer_invoice`.
   *
   * Previewing writes nothing: no attachment row, no blob, no invoice
   * or reference number (an invoice without them gets the values
   * generation would mint, which are not persisted), no state change
   * and no domain event. Every preview, including one that ends in a
   * build error, is audit-logged with the invoice's configuration.
   *
   * `error_code` values returned with `422`: the
   * `TransferInvoiceBuildErrorCode` values. A build error means no
   * document can be produced; `message` says which value is missing
   * or wrong.
   *
   * Requires the `invoice-docs:preview` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `previewTransferInvoice()` instead.
   *
   * This method doesn't expect any request body.
   */
  previewTransferInvoice$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<TransferInvoicePreview>> {

    const rb = new RequestBuilder(this.rootUrl, AdminService.PreviewTransferInvoicePath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<TransferInvoicePreview>;
      })
    );
  }

  /**
   * Preview the transfer invoice built for an invoice.
   *
   * Builds the transfer invoice for the invoice exactly as it would be
   * built for sending: the seller's own invoice, with the invoice's
   * InvoiceConfiguration as the payee, Corona Capital as the sender,
   * Corona's `new_number` and `new_reference_number` as the invoice
   * number and payment reference, and the configuration's transfer
   * statement added. The source track is chosen by data (see
   * `TransferInvoiceTrack`).
   *
   * The response carries the track, the source Finvoice (on the
   * `original` track), the transfer invoice Finvoice, the schema
   * validation of both, and the findings: disagreements between the
   * original and the invoice record, and schema defects. A finding
   * does not stop the document from being built. The rendered PDF of
   * the same document is
   * `POST /admin/invoices/{id}/preview-pdf?kind=transfer_invoice`.
   *
   * Previewing writes nothing: no attachment row, no blob, no invoice
   * or reference number (an invoice without them gets the values
   * generation would mint, which are not persisted), no state change
   * and no domain event. Every preview, including one that ends in a
   * build error, is audit-logged with the invoice's configuration.
   *
   * `error_code` values returned with `422`: the
   * `TransferInvoiceBuildErrorCode` values. A build error means no
   * document can be produced; `message` says which value is missing
   * or wrong.
   *
   * Requires the `invoice-docs:preview` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `previewTransferInvoice$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  previewTransferInvoice(params: {
    id: number;
    context?: HttpContext
  }
): Observable<TransferInvoicePreview> {

    return this.previewTransferInvoice$Response(params).pipe(
      map((r: StrictHttpResponse<TransferInvoicePreview>) => r.body as TransferInvoicePreview)
    );
  }

}
